import {IButtonProps, IColumn, IDetailsRowProps, IRenderFunction, SelectionMode,} from "@fluentui/react";
import {DocumentNode} from "graphql";
import {MutableRefObject, ReactElement} from "react";
import {PagedResult, PagingModel, SavedFile} from "../API";
import {TenantResource} from "../enums/TenantResource";
import {ITenantResourceQuery} from "../interfaces/ITenantResourceQuery";
import {hr} from "../locales/hr";
import {FormAction, TWithStoragesAndOriginals} from "../types/TenantTypes";
import {SupportCodeLanguage} from "./CMElement";
import {TBeforeSubmitFormCallback} from "../hooks/useTenantResourceSingle";

export type ElementType =
    | "CUSTOMSEARCHBOX"
    | "MULTILINETEXT"
    | "NUMBER"
    | "TEXT"
    | "DATETIME"
    | "TIME"
    | "FILE_UPLOAD"
    | "CHECKBOX"
    | "TITLE"
    | "DATE_AND_TIME"
    | "ENUM_DROPDOWN"
    | "HEADER"
    | "DROPDOWN"
    | "PADDING"
    | "GRAPHQL_SEARCH"
    | "HIDDEN_ELEMENT"
    | "TENANT_SEARCH_BOX"
    | "SQL"
    | "CUSTOM_RENDER"
    | "BUTTON_ELEMENT"
    | "BLOB"
    | "GROUP_ELEMENT"
    | "VERTICAL_GROUP_ELEMENT"
    | "GRID_ELEMENT"
    | "MASKED_TEXT"
    | "NUMERIC_ELEMENT"
    | "EXPANDED_ELEMENT"
    | "SPACER_ELEMENT"
    | "CMEDITOR_ELEMENT"
    | "PATH_ELEMENT"
    | "RICH_TEXT";

export type ElementDependency<T> = {
    key: keyof T;
    // vrati vrijednost ili I storage I vrijednost, npr ako želimo sve obrisati, {storage: undefined, value: undefined}
    onChanged: (
        data: TWithStoragesAndOriginals<T>,
        initialized: boolean,
    ) => Promise<any | { storage: any; value: any }>;
};

interface IElement<TDto> {
    type: ElementType;
    description: string;
}

export class CustomSearchBoxElement<T, TDto> implements IElement<TDto> {
    description: string;
    resource?: TenantResource;
    url: string | ((data: TDto) => string);
    key: string;
    titleKey: string;
    subtitleKey?: string;
    customSearchBoxType: "DEFAULT_CRUD" | "TENANT_OLD";
    inlineFormResource?: TenantResource;
    inlineFormForm?: ITenantFormLayout<T>;
    query?: ITenantResourceQuery<T>;
    additionalIntParameter?: (dto?: TDto) => number | undefined;
    additionalStringParameter?: (dto?: TDto) => string | undefined;
    orderBy?: keyof T;
    orderByDescending?: boolean;
    makeSubtitle?: (t: T) => string;
    ifEmptyShowValue?: boolean;
    showCancelButton?: boolean;
    subtitleIsImage?: boolean;
    dontShowValueInLabel?: boolean;
    makeSubSubtitle?: (a: T) => string;
    ifEmptyShowValueAtKey?: keyof TDto;
    highlighIf?: (t: TDto) => boolean;
    prefillSubtitleIfEmpty?: boolean;
    queryFromDto?: (dto: TDto) => ITenantResourceQuery<T> | undefined;
    makeTitle?: (t: T) => string;
    headers?: { [key: string]: string };
    paging?: Partial<PagingModel>;

    constructor({
                    resource,
                    url,
                    key,
                    titleKey,
                    customSearchBoxType,
                    subtitleKey,
                    inlineFormResource,
                    inlineFormForm,
                    query,
                    additionalIntParameter,
                    additionalStringParameter,
                    orderBy,
                    orderByDescending,
                    makeSubtitle,
                    ifEmptyShowValue,
                    showCancelButton,
                    subtitleIsImage,
                    dontShowValueInLabel,
                    makeSubSubtitle,
                    ifEmptyShowValueAtKey,
                    highlighIf,
                    prefillSubtitleIfEmpty,
                    description,
                    queryFromDto,
                    makeTitle,
                    headers,
                    paging,
                }: {
        resource?: TenantResource;
        url?: string | ((data: TDto) => string);
        key: keyof T;
        titleKey: keyof T;
        customSearchBoxType: "DEFAULT_CRUD" | "TENANT_OLD";
        subtitleKey?: keyof T;
        inlineFormResource?: TenantResource;
        inlineFormForm?: ITenantFormLayout<T>;
        query?: ITenantResourceQuery<T>;
        additionalIntParameter?: (dto?: TDto) => number | undefined;
        additionalStringParameter?: (dto?: TDto) => string | undefined;
        orderBy?: keyof T;
        orderByDescending?: boolean;
        ifEmptyShowValue?: boolean;
        showCancelButton?: boolean;
        makeSubtitle?: (t: T) => string;
        subtitleIsImage?: boolean;
        dontShowValueInLabel?: boolean;
        makeSubSubtitle?: (a: T) => string;
        ifEmptyShowValueAtKey?: keyof TDto;
        highlighIf?: (t: TDto) => boolean;
        prefillSubtitleIfEmpty?: boolean;
        description?: string;
        queryFromDto?: (dto: TDto) => ITenantResourceQuery<T> | undefined;
        makeTitle?: (t: T) => string;
        headers?: { [key: string]: string };
        paging?: Partial<PagingModel>;
    }) {
        this.resource = resource;
        if (!this.resource && !url) {
            throw Error("Postavite resurs ili url!");
        } else if (this.resource) {
            this.url = this.resource + "/List";
        } else if (url) {
            this.url = url;
        } else {
            this.url =
                "NIJE POSTAVLJEN URL!, niti iz TenantResource niti direktno iz url";
        }
        this.key = key as string;
        this.titleKey = titleKey as string;
        this.subtitleKey = subtitleKey as string;
        this.customSearchBoxType = customSearchBoxType;
        this.inlineFormResource = inlineFormResource;
        this.inlineFormForm = inlineFormForm;
        this.query = query;
        this.additionalIntParameter = additionalIntParameter;
        this.additionalStringParameter = additionalStringParameter;
        this.orderBy = orderBy;
        this.orderByDescending = orderByDescending;
        this.makeSubtitle = makeSubtitle;
        this.ifEmptyShowValue = ifEmptyShowValue;
        this.showCancelButton = showCancelButton;
        this.subtitleIsImage = subtitleIsImage;
        this.dontShowValueInLabel = dontShowValueInLabel;
        this.makeSubSubtitle = makeSubSubtitle;
        this.ifEmptyShowValueAtKey = ifEmptyShowValueAtKey;
        this.highlighIf = highlighIf;
        this.prefillSubtitleIfEmpty = prefillSubtitleIfEmpty;
        this.description = description || "";
        this.queryFromDto = queryFromDto;
        this.makeTitle = makeTitle;
        this.headers = headers;
        this.paging = paging;
    }

    type: ElementType = "CUSTOMSEARCHBOX";
}

export class TenantSearchBoxElement<T, TDto> implements IElement<TDto> {
    key: string;
    titleKey: string;
    subtitleKey?: string;
    resource: TenantResource;
    description: string;
    additionalIntParameter?: (dto?: TDto) => number | undefined;
    additionalStringParameter?: (dto?: TDto) => string | undefined;
    headers?: { [key: string]: string };
    paging?: Partial<PagingModel>;

    constructor({
                    resource,
                    key,
                    titleKey,
                    subtitleKey,
                    description,
                    additionalIntParameter,
                    additionalStringParameter,
                    headers,
                    paging,
                }: {
        resource: TenantResource;
        key: keyof T;
        titleKey: keyof T;
        subtitleKey?: keyof T;
        description?: string;
        additionalIntParameter?: (dto?: TDto) => number | undefined;
        additionalStringParameter?: (dto?: TDto) => string | undefined;
        headers?: { [key: string]: string };
        paging?: Partial<PagingModel>;
    }) {
        this.key = key as string;
        this.titleKey = titleKey as string;
        this.subtitleKey = subtitleKey as string;
        this.resource = resource;
        this.description = description || "";
        this.additionalIntParameter = additionalIntParameter;
        this.additionalStringParameter = additionalStringParameter;
        this.headers = headers;
        this.paging = paging;
    }

    type: ElementType = "TENANT_SEARCH_BOX";
}

export class MultilineTextElement<TDto> implements IElement<TDto> {
    rows: number;
    maxLength?: number;
    showEditModeToggle: boolean;
    description: string;

    constructor({
                    rows,
                    maxLength,
                    showEditModeToggle,
                    description,
                }: {
        rows?: number;
        maxLength?: number;
        showEditModeToggle?: boolean;
        codeLanguage?: SupportCodeLanguage;
        description?: string;
    }) {
        this.rows = rows ?? 5;
        this.maxLength = maxLength;
        this.showEditModeToggle = showEditModeToggle ?? false;
        this.description = description || "";
    }

    type: ElementType = "MULTILINETEXT";
    transformation?: (v: string) => string;
}

export class NumberElement<TDto> implements IElement<TDto> {
    type: ElementType = "NUMBER";
    description: string;

    constructor({description}: { description?: string }) {
        this.description = description || "";
    }
}

export class SpacerElement<TDto> implements IElement<TDto> {
    type: ElementType = "SPACER_ELEMENT";
    height?: number;
    width?: number;
    description: string;

    constructor({
                    height,
                    width,
                    description,
                }: {
        height?: number;
        width?: number;
        description?: string;
    }) {
        this.height = height;
        this.width = width;
        this.description = description || "";
    }
}

export class ExpandedElement<TDto> implements IElement<TDto> {
    type: ElementType = "EXPANDED_ELEMENT";
    direction: "BOTH" | "HORIZONTAL" | "VERTICAL" = "BOTH";
    description: string;

    constructor({
                    direction = "BOTH",
                    description,
                }: {
        direction: "BOTH" | "HORIZONTAL" | "VERTICAL";
        description?: string;
    }) {
        this.direction = direction;
        this.description = description || "";
    }
}

export class TextFieldElement<TDto> implements IElement<TDto> {
    type: ElementType = "TEXT";
    transformation?: (v: string) => string;
    noLabel?: boolean;
    disabledFormatting?: (item?: TDto) => string;
    description: string;

    constructor({
                    noLabel,
                    disabledFormatting,
                    description,
                }: {
        noLabel?: boolean;
        disabledFormatting?: (item?: TDto) => string;
        description?: string;
    }) {
        this.noLabel = noLabel;
        this.disabledFormatting = disabledFormatting;
        this.description = description || "";
    }
}

export class NumericFieldElement<TDto> implements IElement<TDto> {
    type: ElementType = "NUMERIC_ELEMENT";
    transformation?: (v: string) => string;
    noLabel?: boolean;
    disabledFormatting?: (item?: TDto) => string;
    description: string;

    constructor({
                    noLabel,
                    disabledFormatting,
                    description,
                }: {
        noLabel?: boolean;
        disabledFormatting?: (item?: TDto) => string;
        description?: string;
    }) {
        this.noLabel = noLabel;
        this.disabledFormatting = disabledFormatting;
        this.description = description || "";
    }
}

export class MaskedTextFieldElement<TDto> implements IElement<TDto> {
    type: ElementType = "MASKED_TEXT";
    transformation?: (v: string) => string;
    noLabel?: boolean;
    mask: string;
    description: string;

    constructor({
                    noLabel,
                    mask,
                    description,
                }: {
        noLabel?: boolean;
        mask: string;
        description?: string;
    }) {
        this.noLabel = noLabel;
        this.mask = mask;
        this.description = description || "";
    }
}

export class SQLTextElement<TDto> implements IElement<TDto> {
    type: ElementType = "SQL";
    description: string;

    constructor({description}: { description?: string }) {
        this.description = description || "";
    }
}

export class ButtonElement<TDto> implements IElement<TDto> {
    type: ElementType = "BUTTON_ELEMENT";
    onClick: (dto: TDto, setDto?: (dto: TDto) => void) => void;
    text: string;
    color?: string;
    description: string;
    disabledIf?: (dto: TDto) => boolean;

    constructor({
                    onClick,
                    text,
                    color,
                    description,
                    disabledIf,
                }: {
        onClick: (dto: TDto, setDto?: (dto: TDto) => void) => void;
        text: string;
        color?: string;
        description?: string;
        disabledIf?: (dto: TDto) => boolean;
    }) {
        this.onClick = onClick;
        this.text = text;
        this.color = color;
        this.description = description || "";
        this.disabledIf = disabledIf;
    }
}

export interface IElementWithChildren<TDto> extends IElement<TDto> {
    elements: ITenantFormLayout<TDto>;
}

export class GroupElement<TDto>
    implements IElement<TDto>, IElementWithChildren<TDto> {
    type: ElementType = "GROUP_ELEMENT";
    elements: ITenantFormLayout<TDto>;
    description: string;

    constructor(elements: ITenantFormLayout<TDto>, description?: string) {
        this.elements = elements;
        this.description = description || "";
    }
}

export class VerticalGroupElement<TDto>
    implements IElement<TDto>, IElementWithChildren<TDto> {
    type: ElementType = "VERTICAL_GROUP_ELEMENT";
    elements: ITenantFormLayout<TDto>;
    gapless?: boolean;
    description: string;

    constructor(
        elements: ITenantFormLayout<TDto>,
        gapless?: boolean,
        description?: string,
    ) {
        this.elements = elements;
        this.gapless = gapless;
        this.description = description || "";
    }
}

export class GridElement<TDto>
    implements IElement<TDto>, IElementWithChildren<TDto> {
    type: ElementType = "GRID_ELEMENT";
    elements: ITenantFormLayout<TDto>;
    columnCount: number;
    description: string;

    constructor({
                    elements,
                    columnCount,
                    description,
                }: {
        elements: ITenantFormLayout<TDto>;
        columnCount: number;
        description?: string;
    }) {
        this.elements = elements;
        this.columnCount = columnCount;
        this.description = description || "";
    }
}

export class BlobElement<TDto> implements IElement<TDto> {
    type: ElementType = "BLOB";
    description: string;

    constructor({description}: { description?: string }) {
        this.description = description || "";
    }
}

export class CustomRenderElement<TDto> implements IElement<TDto> {
    type: ElementType = "CUSTOM_RENDER";
    renderer: (dto: TDto, setDto?: (dto: TDto) => void) => JSX.Element;
    validate?: (dto: TDto) => boolean;
    description: string;

    constructor({
                    renderer,
                    validate,
                    description,
                }: {
        renderer: (dto: TDto, setDto?: (dto: TDto) => void) => JSX.Element;
        validate?: (dto: TDto) => boolean;
        description?: string;
    }) {
        this.renderer = renderer;
        this.validate = validate;
        this.description = description || "";
    }
}

export class DateElement<TDto> implements IElement<TDto> {
    type: ElementType = "DATETIME";
    maxDate?: Date;
    short?: boolean;
    utcOnly?: boolean;
    showCancel?: boolean;
    description: string;

    constructor({
                    maxDate,
                    short,
                    utcOnly,
                    showCancel,
                    description,
                }: {
        maxDate?: Date;
        short?: boolean;
        utcOnly?: boolean;
        showCancel?: boolean;
        description?: string;
    }) {
        this.maxDate = maxDate;
        this.short = short;
        this.utcOnly = utcOnly;
        this.showCancel = showCancel;
        this.description = description || "";
    }
}

export class PaddingElement<TDto> implements IElement<TDto> {
    type: ElementType = "PADDING";
    amount: number;
    description: string;

    constructor(amount: number = 10, description?: string) {
        this.amount = amount;
        this.description = description || "";
    }
}

export class DropdownElement<T, TDto> implements IElement<TDto> {
    type: ElementType = "DROPDOWN";
    values: T[];
    key: keyof T;
    titleKey: keyof T;
    showEmpty?: boolean;
    errorIf?: (dto: TDto) => boolean;
    successIf?: (dto: TDto) => boolean;
    description: string;
    multiple?: boolean;

    constructor({
                    values,
                    key,
                    titleKey,
                    showEmpty,
                    errorIf,
                    successIf,
                    description,
                    multiple,
                }: {
        values: T[];
        key: keyof T;
        titleKey: keyof T;
        showEmpty?: boolean;
        errorIf?: (dto: TDto) => boolean;
        successIf?: (dto: TDto) => boolean;
        description?: string;
        multiple?: boolean;
    }) {
        this.values = values;
        this.key = key;
        this.titleKey = titleKey;
        this.showEmpty = showEmpty;
        this.errorIf = errorIf;
        this.successIf = successIf;
        this.description = description || "";
        this.multiple = multiple;
    }
}

export class TimeElement<TDto> implements IElement<TDto> {
    type: ElementType = "TIME";
    description: string;

    constructor({description}: { description?: string }) {
        this.description = description || "";
    }
}

export class FileUploadElement<TDto> implements IElement<TDto> {
    type: ElementType = "FILE_UPLOAD";
    maxXorY?: number;
    nameOverride?: string;
    prependOriginalFileName?: boolean;
    keepOriginalExtension?: boolean;
    additionalDirectorySegments?: string;
    query: (dto: TDto) => ITenantResourceQuery<SavedFile>;
    baseDto: (dto: TDto) => Partial<SavedFile>;
    showUserAuthorization?: boolean;
    description: string;
    showTopBar?: boolean;
    selectionChanged?: (item?: Partial<SavedFile>) => void;

    constructor({
                    maxXorY,
                    nameOverride,
                    prependOriginalFileName,
                    keepOriginalExtension,
                    additionalDirectorySegments,
                    query,
                    baseDto,
                    showUserAuthorization,
                    description,
                    showTopBar,
                    selectionChanged,
                }: {
        maxXorY?: number;
        nameOverride?: string;
        prependOriginalFileName?: boolean;
        keepOriginalExtension?: boolean;
        additionalDirectorySegments?: string;
        query: (dto: TDto) => ITenantResourceQuery<SavedFile>;
        baseDto: (dto: TDto) => Partial<SavedFile>;
        showUserAuthorization?: boolean;
        description?: string;
        showTopBar?: boolean;
        selectionChanged?: (item?: Partial<SavedFile>) => void;
    }) {
        this.maxXorY = maxXorY;
        this.nameOverride = nameOverride;
        this.prependOriginalFileName = prependOriginalFileName;
        this.keepOriginalExtension = keepOriginalExtension;
        this.additionalDirectorySegments = additionalDirectorySegments;
        this.query = query;
        this.baseDto = baseDto;
        this.showUserAuthorization = showUserAuthorization;
        this.description = description || "";
        this.showTopBar = showTopBar;
        this.selectionChanged = selectionChanged;
    }
}

export class CheckboxElement<TDto> implements IElement<TDto> {
    type: ElementType = "CHECKBOX";
    disabled?: boolean;
    tristate?: boolean;
    useString?: boolean;
    description: string;

    constructor({
                    disabled,
                    tristate,
                    useString,
                    description,
                }: {
        disabled?: boolean;
        tristate?: boolean;
        useString?: boolean;
        description?: string;
    }) {
        this.disabled = disabled;
        this.tristate = tristate;
        this.useString = useString;
        this.description = description || "";
    }
}

export class TitleElement<TDto> implements IElement<TDto> {
    type: ElementType = "TITLE";
    titleKey?: string;
    hideLabel?: boolean;
    alternativeTitleKey?: string;
    titleFormatter?: (dto?: TDto) => any;
    hideIfEmpty?: boolean;
    divided?: boolean;
    horizontal?: boolean;
    alignRight?: boolean;
    description: string;

    constructor({
                    titleKey,
                    hideLabel,
                    alternativeTitleKey,
                    titleFormatter,
                    hideIfEmpty,
                    divided,
                    horizontal,
                    alignRight,
                    description,
                }: {
        horizontal?: boolean;
        titleKey?: string;
        hideIfEmpty?: boolean;
        hideLabel?: boolean;
        alternativeTitleKey?: string;
        titleFormatter?: (dto?: TDto) => any;
        divided?: boolean;
        alignRight?: boolean;
        description?: string;
    }) {
        this.titleKey = titleKey;
        this.hideLabel = hideLabel;
        this.alternativeTitleKey = alternativeTitleKey;
        this.titleFormatter = titleFormatter;
        this.hideIfEmpty = hideIfEmpty;
        this.divided = divided;
        this.horizontal = horizontal;
        this.alignRight = alignRight;
        this.description = description || "";
    }
}

export class DateAndTimeElement<TDto> implements IElement<TDto> {
    type: ElementType = "DATE_AND_TIME";
    description: string;

    constructor({description}: { description?: string }) {
        this.description = description || "";
    }
}

export class HeaderElement<TDto> implements IElement<TDto> {
    type: ElementType = "HEADER";
    title?: string;
    description: string;

    constructor({
                    title,
                    description,
                }: {
        title?: string;
        description?: string;
    }) {
        this.title = title;
        this.description = description || "";
    }
}

export class EnumDropdownElement<TDto> implements IElement<TDto> {
    type: ElementType = "ENUM_DROPDOWN";
    enumData: any;
    showCancel?: boolean;
    iconFromValue?: (value: any) => any;
    rightSideIconButtonProps?: IButtonProps;
    multiple?: boolean;
    inline?: boolean;
    description: string;

    constructor({
                    enumData,
                    showCancel,
                    iconFromValue,
                    rightSideIconButtonProps,
                    multiple,
                    inline,
                    description,
                }: {
        enumData: any;
        showCancel?: boolean;
        iconFromValue?: (value: any) => any;
        rightSideIconButtonProps?: IButtonProps;
        multiple?: boolean;
        inline?: boolean;
        description?: string;
    }) {
        this.enumData = enumData;
        this.showCancel = showCancel;
        this.iconFromValue = iconFromValue;
        this.rightSideIconButtonProps = rightSideIconButtonProps;
        this.multiple = multiple;
        this.inline = inline;
        this.description = description || "";
    }
}

export class GraphQLSearchElement<TDto> implements IElement<TDto> {
    type: ElementType = "GRAPHQL_SEARCH";
    query: DocumentNode;
    queryKey: string;
    queryResultKey: string;
    queryResultName: string;
    description: string;

    constructor({
                    query,
                    queryKey,
                    queryResultKey,
                    queryResultName,
                    description,
                }: {
        query: DocumentNode;
        queryKey: string;
        queryResultKey: string;
        queryResultName: string;
        description?: string;
    }) {
        this.query = query;
        this.queryKey = queryKey;
        this.queryResultKey = queryResultKey;
        this.queryResultName = queryResultName;
        this.description = description || "";
    }
}

export class HiddenElement<TDto> implements IElement<TDto> {
    type: ElementType = "HIDDEN_ELEMENT";
    description: string;

    constructor({description}: { description?: string }) {
        this.description = description || "";
    }
}

export class PathElement<TDto> implements IElement<TDto> {
    type: ElementType = "PATH_ELEMENT";
    description: string;

    constructor({description}: { description?: string }) {
        this.description = description || "";
    }
}

export class CMEditorElement<TDto> implements IElement<TDto> {
    type: ElementType = "CMEDITOR_ELEMENT";
    codeLanguage: SupportCodeLanguage;
    minHeight?: number;
    description: string;
    tsDefinitions?: string;
    tsDefinitionsFromDto?: (dto?: TDto) => string | undefined;

    constructor({
                    codeLanguage,
                    minHeight,
                    description,
                    tsDefinitions,
                    tsDefinitionsFromDto,
                }: {
        codeLanguage: SupportCodeLanguage;
        minHeight?: number;
        description?: string;
        tsDefinitions?: string;
        tsDefinitionsFromDto?: (dto?: TDto) => string | undefined;
    }) {
        this.codeLanguage = codeLanguage;
        this.minHeight = minHeight;
        this.description = description || "";
        this.tsDefinitions = tsDefinitions;
        this.tsDefinitionsFromDto = tsDefinitionsFromDto;
    }
}

export class RichTextElement<TDto> implements IElement<TDto> {
    type: ElementType = "RICH_TEXT";
    minHeight?: number;
    description: string;

    constructor({minHeight, description}: { minHeight?: number; description?: string }) {
        this.minHeight = minHeight;
        this.description = description || "";
    }
}

export interface ITenantFormLayoutDefinition<TDto> {
    type?: IElement<TDto>;
    actualField?: keyof TDto;
    translationKey?: keyof typeof hr;
    disabled?: boolean;
    dependsOn?: ElementDependency<TDto>[];
    minWidth?: number;
    maxWidth?: number;
    onChanged?: (dto?: TDto) => void;
    disabledIf?: (dto?: TDto) => boolean;
    addon?: (dto?: TDto) => React.ReactNode;
    showErrorIf?: (dto?: TDto) => boolean;
    hiddenIf?: (dto?: TDto) => boolean;
}

export type ITenantFormLayout<TDto> =
    | {
    [key in keyof Partial<TDto>]: ITenantFormLayoutDefinition<TDto> | null;
}
    | { [key: string]: ITenantFormLayoutDefinition<TDto> | null }
    | { [key: string]: any };

//@ts-ignore
export interface ITenantColumn<T> extends IColumn {
    key: keyof T | any;
    fieldName: keyof T | any;
    onRender?: (item: T) => any;
}

export enum SubmissionType {
    Post,
    Put,
    Delete,
}

export interface ITenantList<TDatabaseModel, TDto, TReturn> {
    query?: ITenantResourceQuery<TReturn>;
    resource: TenantResource;
    minimalTopBar?: boolean;
    addingTitle?: keyof typeof hr;
    ignoreTenancy?: boolean;
    editingTitle?: keyof typeof hr;
    beforeSubmitForm?: TBeforeSubmitFormCallback<TDto, TReturn>; // boolean vrati kao continue?
    form?: ITenantFormLayout<TDto>;
    baseDto: TWithStoragesAndOriginals<Partial<TDto>>;
    onAddDialogOpen?: () => void | Promise<void>;
    columns: ITenantColumn<TReturn>[];
    itemInvoked?: (t: TReturn) => void;
    queryIsRequired?: boolean;
    compactPaging?: boolean;
    footer?: (items?: TReturn[]) => ReactElement | null;
    allowSearching?: boolean;
    pad?: boolean;
    additionalSelectionCommands?: (item: TReturn) => JSX.Element[];
    allowedActions?: FormAction[];
    hideGrouping?: boolean;
    id?: string;
    children?: (reload: () => void, selectedItem: TReturn) => ReactElement | null;
    additionalCommands?: JSX.Element[];
    stretchForm?: boolean;
    className?: string;
    preserveSelection?: boolean;
    horizontal?: boolean;
    formChildren?: (dto?: TDto) => JSX.Element | null;
    orderBy?: keyof TDatabaseModel;
    orderDescending?: boolean;
    minFormWidth?: number | string;
    /// storage se radi iz npr za currencyId storage je currencyIdstorage, cijeli objekt mora biti u currency: {} i on se pretvara u currencyIdstorage: {}
    storageMapping?: (dto?: TDto) => any;
    getId?: (data: any) => any;
    renderRow?: IRenderFunction<IDetailsRowProps>;
    allowedGroupKeys?:
        | Array<keyof TReturn>
        | {
        [key in keyof Partial<TReturn>]: (
            val: Partial<TReturn>[key],
            dto: Partial<TReturn>,
        ) => string;
    };
    allowShowAll?: boolean;
    sidebar?: JSX.Element;
    style?: React.CSSProperties;
    keepFilterColumns?: boolean;
    defaultGroups?: Array<keyof TReturn>;
    hideTopBar?: boolean;
    headers?: { [key: string]: string };
    reloadRef?: MutableRefObject<
        (() => Promise<PagedResult<TReturn>>) | undefined | null
    >;
    reloadSingleRef?: MutableRefObject<
        ((item: TReturn) => Promise<TReturn>) | undefined
    >;
    editItemRef?: MutableRefObject<((item: TReturn) => void) | undefined>;
    deleteItemRef?: MutableRefObject<
        ((item: TReturn) => Promise<void>) | undefined
    >;
    resultIsEmpty?: () => void;
    resultsChanged?: (items: TReturn[]) => void;
    selectionModeOverride?: SelectionMode;
    additionalIntParameter?: number;
    hideNoItemsFooter?: boolean;
    afterFormAction?: (formAction: FormAction, dto: TReturn) => void;
    selectionChanged?: (item?: Partial<TReturn>) => void;
    selectionItemsChanged?: (items: TReturn[]) => void;
    title?: keyof typeof hr | string;
    dontUseStickyTopBar?: boolean;
    listStyle?: React.CSSProperties;
    wrapStyle?: React.CSSProperties;
    hideGroupTitle?: boolean;
    makeGroupHeaderAddonText?: (ts: TReturn[]) => ReactElement | null;
    additionalStringParameter?: string;
    debug?: boolean;
    hideIfEmpty?: boolean;
    disablePaging?: boolean;
    disableWrappingOnChildren?: boolean;
    afterInsertFocusInserted?: boolean;
    quickFilters?: ITenantFormLayout<TDto>;
    disabled?: boolean;
    clearSelectionRef?: MutableRefObject<() => void>;
    dontSubmitOnEnter?: boolean;
    hideSaveAndCloseOnForm?: boolean;
}
